import { api } from 'constants/constant';
import { getHeaders, getUserDetails } from 'auth/auth';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { pipelineAPI } from './pipeline';

let isFirstTimeUserInFlight = null;
let isFirstTimeUserCache = null;
let isFirstTimeUserCacheKey = null;
let isFirstTimeUserCacheAt = 0;
const IS_FIRST_TIME_USER_CACHE_TTL_MS = 30 * 1000;

const withB2cPrefix = (path = '') => {
  if (!path) return '/b2c';
  if (path.startsWith('/b2c')) return path;
  if (path.startsWith('/')) return `/b2c${path}`;
  return `/b2c/${path}`;
};

export const onboardingAPI = createApi({
  reducerPath: 'onboarding',
  baseQuery: fetchBaseQuery({ baseUrl: api, credentials: 'include' }),
  tagTypes: ['getOnboarding'],
  endpoints: (builder) => ({
    // generate application id after successful onboarding
    createApplication: builder.mutation({
      query: ({ data }) => {
        return {
          url: withB2cPrefix('/loan/application'),
          method: 'POST',
          headers: getHeaders(),
          body: data,
        };
      },
    }),
    createNewApplication: builder.mutation({
      query: ({ data }) => {
        return {
          url: withB2cPrefix("/loan/application/new"),
          method: "POST",
          headers: getHeaders(),
          body: data,
        };
      },
      // The pipelineAPI slice owns the My Applications list — RTK Query
      // tags are scoped per slice, so we have to invalidate it explicitly
      // here. Without this, a freshly-created application doesn't appear
      // in /pipeline until a hard reload.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(pipelineAPI.util.invalidateTags(['updatePipeline']));
        } catch {
          // Creation failed; the user stays on whatever screen they were
          // on and pipeline data is unchanged anyway.
        }
      },
    }),
    // post onboarding data to API
    createOnboarding: builder.mutation({
      query: ({ data }) => {
        return {
          url: withB2cPrefix('user/onboarding_profile'),
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getOnboarding'],
    }),
    // update/put onboarding data to API
    updateOnboarding: builder.mutation({
      query: ({ data }) => {
        return {
          url: withB2cPrefix('user/onboarding_profile'),
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getOnboarding'],
    }),
    // fetch onboarding data from API
    getOnboardingData: builder.query({
      query: () => {
        return {
          url: withB2cPrefix('user/onboarding_profile/'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['getOnboarding'],
    }),
    getOnboarding: builder.query({
      query: () => {
        return {
          url: withB2cPrefix('/loan/application_dropdowns/application_status'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    isFirstTimeUser: builder.mutation({
      queryFn: async (_arg, _queryApi, _extraOptions, baseQuery) => {
        const { userId, userEmail, firstName, lastName } = getUserDetails();
        const userIdString = userId ? String(userId) : '';
        const requestKey = `${userIdString}:${userEmail || ''}`;

        if (!userIdString) {
          return { error: { status: 400, data: { message: 'Missing user_id' } } };
        }

        const now = Date.now();
        if (
          isFirstTimeUserCache &&
          isFirstTimeUserCacheKey === requestKey &&
          now - isFirstTimeUserCacheAt < IS_FIRST_TIME_USER_CACHE_TTL_MS
        ) {
          return { data: isFirstTimeUserCache };
        }

        if (isFirstTimeUserInFlight && isFirstTimeUserCacheKey === requestKey) {
          return isFirstTimeUserInFlight;
        }

        isFirstTimeUserCacheKey = requestKey;
        isFirstTimeUserInFlight = (async () => {
          const result = await baseQuery({
            url: withB2cPrefix('/user/is_first_time_user'),
            method: 'POST',
            headers: getHeaders(),
            body: {
              user_id: userIdString,
              source: 'auth0',
              email: userEmail,
              first_name: firstName,
              last_name: lastName,
            },
          });

          if (result.data) {
            isFirstTimeUserCache = result.data;
            isFirstTimeUserCacheAt = Date.now();
            return { data: result.data };
          }

          return { error: result.error };
        })();

        try {
          return await isFirstTimeUserInFlight;
        } finally {
          isFirstTimeUserInFlight = null;
        }
      },
    }),
    //get list of loan officers
    getLoanOfficers: builder.query({
      query: () => {
        return {
          url: withB2cPrefix('/user/loan_officers/'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    //get user details
    getUserInfo: builder.query({
      query: () => {
        const { userId } = getUserDetails();
        return {
          url: withB2cPrefix(`/user/user_profiles/${userId}`),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    getLoanOfficerDetails: builder.query({
      query: () => {
        return {
          url: withB2cPrefix('user/get_loan_officer_details'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    getUserProfileImage: builder.query({
      query: (userId) => {
        return {
          url: withB2cPrefix(`user/fetch_user_profile_url/${userId}`),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    uploadConsumerProfilePhoto: builder.mutation({
      queryFn: async ({ file }) => {
        if (!(file instanceof File) && !(file instanceof Blob)) {
          return { error: { status: 400, data: { message: 'Invalid file payload' } } };
        }

        const formData = new FormData();
        formData.append('file', file, file?.name || 'profile-photo');

        try {
          const response = await fetch(`${api}/b2c/user/profile-photo`, {
            method: 'POST',
            body: formData,
            credentials: 'include',
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: 'Profile photo upload failed' }));
            return { error: { status: response.status, data: errorData } };
          }

          const data = await response.json();
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: error.message } };
        }
      },
    }),
  }),
});

export const {
  useCreateApplicationMutation,
  useCreateNewApplicationMutation,
  useGetLoanOfficersQuery,
  useIsFirstTimeUserMutation,
  useGetUserInfoQuery,
  useCreateOnboardingMutation,
  useUpdateOnboardingMutation,
  useLazyGetOnboardingQuery,
  useGetOnboardingDataQuery,
  useGetLoanOfficerDetailsQuery,
  useGetUserProfileImageQuery,
  useUploadConsumerProfilePhotoMutation,
} = onboardingAPI;
